import React from 'react';

interface GaugeProps {
  value: number; // 0 to 100
}

const Gauge: React.FC<GaugeProps> = ({ value }) => {
  const clampedValue = Math.min(Math.max(value, 0), 100);
  // -90 degrees (left) to 90 degrees (right), with 0 being straight up.
  const angle = (clampedValue / 100) * 180 - 90;

  // Converts polar coordinates (angle, radius) to Cartesian (x, y).
  // Note: The coordinate system is rotated by -90 degrees, so 0 degrees is at the top.
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  // Describes an SVG arc path.
  const getArc = (startAngle: number, endAngle: number, radius: number) => {
    const start = polarToCartesian(100, 100, radius, startAngle);
    const end = polarToCartesian(100, 100, radius, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y
    ].join(' ');
  };

  const segments = [
    { color: '#EF4444', start: 0, end: 25 },   // Red-500 (Extreme Fear)
    { color: '#F97316', start: 25, end: 45 },  // Orange-500 (Fear)
    { color: '#EAB308', start: 45, end: 55 },  // Yellow-500 (Neutral)
    { color: '#84CC16', start: 55, end: 75 },  // Lime-500 (Greed)
    { color: '#22C55E', start: 75, end: 100 }, // Green-500 (Extreme Greed)
  ];

  return (
    <div className="relative w-full" style={{ paddingTop: '50%' }}>
      <svg
        className="absolute top-0 left-0 w-full h-full"
        viewBox="0 0 200 100"
        preserveAspectRatio="xMidYMid meet"
        aria-label={`Gauge with value ${value} out of 100`}
        role="img"
      >
        <g>
          {segments.map((segment, index) => (
            <path
              key={index}
              // FIX: Map the value range [0, 100] to the angle range [270, 450].
              // This draws a top semi-circle from left to right, matching the needle's logic.
              // The original calculation (segment.start * 1.8) drew an incorrect right-side semi-circle.
              d={getArc(segment.start * 1.8 + 270, segment.end * 1.8 + 270, 80)}
              stroke={segment.color}
              strokeWidth="20"
              fill="none"
            />
          ))}
        </g>
        {/* Arrow Indicator */}
        <g transform={`rotate(${angle} 100 100)`}>
          <polygon
            // A triangle pointing upwards from the center.
            // Tip is at (100, 35), base is from (95, 85) to (105, 85).
            // This moves the arrow closer to the arc without changing its length.
            points="100,35 95,85 105,85"
            fill="#E0E1DD"
          />
        </g>
      </svg>
    </div>
  );
};

export default Gauge;